import type { ToolGroupDefinition } from "../../AgentToolsService";
import { fillForm } from "./fillForm";
import { getFormState } from "./getFormState";
import { selectDropdown } from "./selectDropdown";
import { typeText } from "./typeText";
import { waitAndClick } from "./waitAndClick";
import { waitForElement } from "./waitForElement";

/**
 * Register all browser interaction tools
 * These tools are grouped together and hidden from initial context
 */
export function registerBrowserTools(): Record<string, ToolGroupDefinition> {
  return {
    browser_tools: {
      name: "browser_tools",
      description:
        "Tools for interacting with the browser DOM. These tools allow you to fill forms, click buttons, type text, select dropdowns, and inspect the current page state. Use get_tool_info with group='browser_tools' to access these tools.",
      tools: {
        wait_and_click: {
          name: "wait_and_click",
          description:
            "Wait for an element to appear on the page and then click it. Use this to interact with buttons, links, toggles, and checkboxes. The element can be found by text content, CSS selector, or buttonLabel (also matches aria-label on toggles). Use withinSelector to scope the search to a specific container (e.g., a drawer or modal). For toggles/switches/checkboxes, prefer fill_form with the field name from get_form_state (e.g. { graduation_enabled: true })—it is more reliable than clicking the label text. For submit/save actions, call get_form_state first and use the exact button text from form.buttons (e.g. form.buttons[].text) as buttonLabel—do not assume 'Update' or 'Submit'; the form may show 'Save' or another label.",
          parameters: {
            selector:
              "string (optional - CSS selector to find the element, e.g., 'button[aria-label=\"Edit\"]')",
            text: "string (optional - text content to find the element, e.g., 'Edit' or 'Add Rule')",
            buttonLabel: "string (optional - button label/name to find, e.g., 'Edit' or 'Add Rule')",
            withinSelector:
              "string (optional - CSS selector for a container to search within, e.g., 'div[aria-roledescription=\"drawer\"]' to search only within a drawer)",
            timeout: "number (optional - maximum time to wait in milliseconds, default 5000)",
          },
          execute: async (args: any) => {
            return await waitAndClick(args);
          },
        },
        fill_form: {
          name: "fill_form",
          description:
            "Fill a form with field values. This tool can fill input fields, textareas, and select dropdowns. Fields can be identified by name attribute, id, or label association.",
          parameters: {
            formSelector:
              "string (optional - CSS selector for the form element. If not provided, the first form in the container will be used)",
            fields:
              "object (required - object mapping field names to values, e.g., {name: 'John', email: 'john@example.com'})",
            withinSelector:
              "string (optional - CSS selector for a container to search within, e.g., 'div[aria-roledescription=\"drawer\"]' to search only within a drawer)",
            timeout: "number (optional - maximum time to wait in milliseconds, default 5000)",
          },
          execute: async (args: any) => {
            return await fillForm(args);
          },
        },
        type_text: {
          name: "type_text",
          description:
            "Type text into an input or textarea field character by character, simulating realistic typing. This triggers keyboard and input events.",
          parameters: {
            selector: "string (required - CSS selector for the input/textarea element)",
            text: "string (required - text to type into the field)",
            withinSelector:
              "string (optional - CSS selector for a container to search within, e.g., 'div[aria-roledescription=\"drawer\"]' to search only within a drawer)",
            clearFirst: "boolean (optional - whether to clear the field before typing, default false)",
            timeout: "number (optional - maximum time to wait in milliseconds, default 5000)",
          },
          execute: async (args: any) => {
            return await typeText(args);
          },
        },
        select_dropdown: {
          name: "select_dropdown",
          description:
            "Select an option in a dropdown/select element. Options can be found by value or text content.",
          parameters: {
            selector: "string (required - CSS selector for the select element)",
            value: "string (optional - value of the option to select)",
            text: "string (optional - text content of the option to select)",
            withinSelector:
              "string (optional - CSS selector for a container to search within, e.g., 'div[aria-roledescription=\"drawer\"]' to search only within a drawer)",
            timeout: "number (optional - maximum time to wait in milliseconds, default 5000)",
          },
          execute: async (args: any) => {
            return await selectDropdown(args);
          },
        },
        wait_for_element: {
          name: "wait_for_element",
          description:
            "Wait for an element to appear on the page. This tool can wait for elements by CSS selector or text content, and optionally check visibility.",
          parameters: {
            selector: "string (optional - CSS selector to find the element)",
            text: "string (optional - text content to find the element)",
            visible: "boolean (optional - whether the element must be visible, default true)",
            timeout: "number (optional - maximum time to wait in milliseconds, default 5000)",
          },
          execute: async (args: any) => {
            return await waitForElement(args);
          },
        },
        get_form_state: {
          name: "get_form_state",
          description:
            "Get the current state of a form or wizard on the page. This tool can identify all fields, their current values, labels, required status, and navigation buttons. For wizards, it can also identify the current step, total steps, and whether you can navigate forward or back. Use this tool to understand what fields are available before filling them in. Excludes the Help & assistant side panel — it reflects main app content only.",
          parameters: {
            formSelector:
              "string (optional - CSS selector for the form element. If not provided, the first form or wizard in the container will be used)",
            withinSelector:
              "string (optional - CSS selector for a container to search within, e.g., 'div[aria-roledescription=\"drawer\"]' to search only within a drawer or modal)",
          },
          execute: async (args: any) => {
            return await getFormState(args);
          },
        },
      },
      hidden: true,
    },
  };
}
